import useMapboxAuth from "../hooks/useMapboxAuth";
import {
    UnauthenticatedTemplate,
    AuthenticatedTemplate,
    ReadAccessUnauthorizedTemplate,
    ReadAccessAuthorizedTemplate,
} from "../hooks/useAuth";
import { useTheme } from "../../contexts/ThemeContext";
import { DataTable } from "../map/DataTable";
import { ConsolidatedDataTable } from "../map/ConsolidatedDataTable";
import {
    useTransmissionLineData,
    useSubstationData,
    useGeneratorData,
    useQueueInsights,
} from "../../contexts/QueueInsightsContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/Tabs";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuItem,
    DropdownMenuContent,
} from "../ui/DropdownMenu";
import QueueInsightsMap from "../map/QueueInsightsMap";
import { Separator } from "../ui/Separator";
import { useState, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { substationColumns } from "../map/SubstationLayerColumns";
import { transmissionLineColumns } from "../map/TransmissionLineLayerColumns";
import { queueIsoColumns } from "../map/QueueIsoColumns";
import { queueProjectColumns } from "../map/QueueProjectColumns";
import { formatQueueIsoData } from "../../utils/queue-iso-utils";
import { QueueInsightsGlobalFilter } from "../filters/QueueInsightsGlobalFilter";
import { useQueueCharts } from "../hooks/useQueueCharts";
import {
    ISOFilterOptions,
    ISOFilters,
} from "../constants/queueInsightsConstants";

type TableFilterOptions = "all" | "selected" | "visible";

const QueueInsightsPage = ({ isPageExpanded }: { isPageExpanded: boolean }) => {
    const { mapboxAccessToken } = useMapboxAuth();
    const { currentTheme } = useTheme();
    const [tableFilterOption, setTableFilterOption] =
        useState<TableFilterOptions>("all");
    const substationData = useSubstationData(tableFilterOption);
    const transmissionLineData = useTransmissionLineData(tableFilterOption);
    const operatingGeneratorsData = useGeneratorData(tableFilterOption);

    const {
        currentISOFilter, setCurrentISOFilter,
        selectedGenerationTypes, setSelectedGenerationTypes,
        selectedStateTypes, setSelectedStateTypes,
        selectedCountyTypes, setSelectedCountyTypes,
        selectedClusterTypes, setSelectedClusterTypes,
        generationTypeOptions, stateTypeOptions,
        countyTypeOptions, clusterTypeOptions,
        isFilterOptionsLoading,
        filteredIsoData,
        resetFilters,
        isLoading,
    } = useQueueInsights();

    const formattedIsoData = useMemo(() => {
        if (!currentISOFilter || !filteredIsoData) return [];
        return formatQueueIsoData(filteredIsoData, currentISOFilter);
    }, [filteredIsoData, currentISOFilter]);

    // Use the charts hook to handle all chart processing and rendering
    const memoizedCharts = useQueueCharts({
        filteredIsoData,
        operatingGeneratorsData,
        currentISOFilter,
        isLoading: isLoading,
        isFilterOptionsLoading,
    });

    return (
        <div className="flex bg-gray-200 dark:bg-gray-900 h-dvh z-0">
            <UnauthenticatedTemplate>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100vh",
                        width: "100%",
                    }}
                >
                    <p>Please sign in to view this content!</p>
                    <button
                        onClick={() => {
                            window.location.href = "/.auth/login/aad";
                        }}
                        className="px-[6px] h-[30px] mt-1 rounded-[3px] bg-accent1 hover:bg-accent2 duration-300 text-white text-[13px] font-semibold"
                    >
                        Sign In
                    </button>
                </div>
            </UnauthenticatedTemplate>
            <AuthenticatedTemplate>
                <ReadAccessUnauthorizedTemplate>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100vh",
                            width: "100%",
                        }}
                    >
                        <p>
                            To view this content, please contact us at{" "}
                            <a href="mailto:services@grid-insights.net">
                                services@grid-insights.net
                            </a>
                            !
                        </p>
                    </div>
                </ReadAccessUnauthorizedTemplate>
                <ReadAccessAuthorizedTemplate>
                    <div className="flex w-full flex-col p-2 h-screen flex-1 min-h-0">
                        <div className="w-full flex flex-row justify-between bg-white dark:bg-[#2D3748] mb-2 p-2 rounded-[4px] items-center px-4 h-[47px]">
                            <div className="flex flex-row gap-4 items-center">
                                <div className="text-[12px] font-semibold text-black dark:text-white">
                                    Filter by ISO
                                </div>
                                <DropdownMenu>
                                    <DropdownMenuTrigger className="py-[2.5px] px-[8px] flex gap-4 border-[1px] border-[#C6C5C5] dark:border-[#7A8690] text-sm rounded-[4px] items-center focus-visible:outline-none hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200">
                                        <div className="text-black dark:text-[#CEE0EF]">
                                            {currentISOFilter ||
                                                "Select an ISO"}
                                        </div>
                                        <ChevronDown className="h-4 w-4 stroke-[#D9D9D9] dark:stroke-[#7A8690]" />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="bg-white dark:bg-[#2D3748]">
                                        {ISOFilters.map(
                                            (ISOFilter: ISOFilterOptions) => (
                                                <DropdownMenuItem
                                                    key={ISOFilter}
                                                    className={`${
                                                        currentISOFilter ===
                                                        ISOFilter
                                                            ? "bg-[#C6D92D] dark:bg-[#C6D92D] hover:bg-[#C6D92D] hover:dark:bg-[#C6D92D] text-[#535353] dark:text-[#2D3748]"
                                                            : "hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] text-[#535353] dark:text-[#CEE0EF]"
                                                    }`}
                                                    onClick={() =>
                                                        setCurrentISOFilter(
                                                            ISOFilter
                                                        )
                                                    }
                                                >
                                                    {ISOFilter}
                                                </DropdownMenuItem>
                                            )
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <button
                                    className="block py-[4px] px-[8px] flex gap-4 border-[1px] border-[#F62A2A] dark:border-[#F62A2A] text-sm rounded-[4px] items-center focus-visible:outline-none hover:bg-[#F62A2A] dark:hover:bg-[#F62A2A] text-[#F62A2A] dark:text-[#F62A2A] hover:text-white dark:hover:text-[#2D3748] font-semibold duration-200"
                                    onClick={resetFilters}
                                >
                                    Reset Filters
                                </button>
                            </div>
                            <div className="flex flex-row gap-6 items-center">
                                <div className="text-[15px] font-semibold text-black dark:text-white">
                                    Generation Type
                                </div>
                                <div className="flex flex-row gap-4 items-center">
                                    <div className="flex flex-row gap-2 items-center">
                                        <div className="w-[12px] h-[12px] bg-[#4361B1] rounded-[2px]" />
                                        <span className="text-[13px] font-semibold text-gray-700 dark:text-[#CEE0EF]">
                                            Solar
                                        </span>
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <div className="w-[12px] h-[12px] bg-[#FFE682] rounded-[2px]" />
                                        <span className="text-[13px] font-semibold text-gray-700 dark:text-[#CEE0EF]">
                                            Wind
                                        </span>
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <div className="w-[12px] h-[12px] bg-[#7ACC7D] rounded-[2px]" />
                                        <span className="text-[13px] font-semibold text-gray-700 dark:text-[#CEE0EF]">
                                            Battery/Storage
                                        </span>
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <div className="w-[12px] h-[12px] bg-[#FF94B0] rounded-[2px]" />
                                        <span className="text-[13px] font-semibold text-gray-700 dark:text-[#CEE0EF]">
                                            Thermal
                                        </span>
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <div className="w-[12px] h-[12px] bg-[#FF8C42] rounded-[2px]" />
                                        <span className="text-[13px] font-semibold text-gray-700 dark:text-[#CEE0EF]">
                                            Other
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row gap-2 mb-2 h-[60px]">
                            <div className="flex flex-row gap-2 w-full justify-between h-full flex-1">
                                <QueueInsightsGlobalFilter
                                    title="Filter by Generation Type"
                                    options={generationTypeOptions}
                                    selectedValues={selectedGenerationTypes}
                                    onChange={setSelectedGenerationTypes}
                                    placeholder="All"
                                    theme={currentTheme}
                                    loading={isFilterOptionsLoading}
                                />
                                <QueueInsightsGlobalFilter
                                    title="Filter by Cluster"
                                    options={clusterTypeOptions}
                                    selectedValues={selectedClusterTypes}
                                    onChange={setSelectedClusterTypes}
                                    placeholder="All"
                                    theme={currentTheme}
                                    loading={isFilterOptionsLoading}
                                />
                                <QueueInsightsGlobalFilter
                                    title="Filter by State"
                                    options={stateTypeOptions}
                                    selectedValues={selectedStateTypes}
                                    onChange={setSelectedStateTypes}
                                    placeholder="All"
                                    theme={currentTheme}
                                    loading={isFilterOptionsLoading}
                                />
                                <QueueInsightsGlobalFilter
                                    title="Filter by County"
                                    options={countyTypeOptions}
                                    selectedValues={selectedCountyTypes}
                                    onChange={setSelectedCountyTypes}
                                    placeholder="All"
                                    theme={currentTheme}
                                    loading={isFilterOptionsLoading}
                                />
                            </div>
                        </div>
                        <div
                            className="flex flex-row min-h-0 flex-1 gap-2 w-full"
                            style={{ height: "40%" }}
                        >
                            {mapboxAccessToken === "" && "Loading…"}
                            {mapboxAccessToken !== "" && (
                                <QueueInsightsMap
                                    mapboxAccessToken={mapboxAccessToken}
                                    theme={currentTheme}
                                    isPageExpanded={isPageExpanded}
                                />
                            )}
                            {memoizedCharts}
                        </div>
                        <div className="flex flex-row gap-2 flex-1 min-h-0 h-[40vh]">
                            <div className="w-1/2 mt-2 bg-white dark:bg-[#2D3748] rounded-[4px] flex flex-col h-full flex-1 min-h-0">
                                <Tabs
                                    defaultValue="queue-projects"
                                    className="w-full h-full flex flex-col flex-1 min-h-0"
                                >
                                    <div className="px-6 flex-none">
                                        <TabsList>
                                            <TabsTrigger value="queue-projects">
                                                Queue Projects
                                            </TabsTrigger>
                                            <Separator
                                                orientation="vertical"
                                                className="h-[15px] w-[1.5px]"
                                            />
                                            <TabsTrigger value="substation">
                                                Substation
                                            </TabsTrigger>
                                            <Separator
                                                orientation="vertical"
                                                className="h-[15px] w-[1.5px]"
                                            />
                                            <TabsTrigger value="transmission">
                                                Transmission Line
                                            </TabsTrigger>
                                            <Separator
                                                orientation="vertical"
                                                className="h-[15px] w-[1.5px]"
                                            />
                                            <DropdownMenu>
                                                <DropdownMenuTrigger className="py-[4px] px-[8px] flex gap-4 border-[1px] border-[#C6C5C5] dark:border-[#7A8690] text-sm rounded-[4px] items-center focus-visible:outline-none hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200">
                                                    <div className="text-black dark:text-[#CEE0EF]">
                                                        {tableFilterOption ===
                                                        "all"
                                                            ? "Show All Features"
                                                            : tableFilterOption ===
                                                                "selected"
                                                              ? "Show Selected Features"
                                                              : "Show Features Visible on Map"}
                                                    </div>
                                                    <ChevronDown className="h-4 w-4 stroke-[#D9D9D9] dark:stroke-[#7A8690]" />
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent className="bg-white dark:bg-[#2D3748]">
                                                    <DropdownMenuItem
                                                        className={`${
                                                            tableFilterOption ===
                                                            "all"
                                                                ? "bg-[#C6D92D] dark:bg-[#C6D92D] hover:bg-[#C6D92D] hover:dark:bg-[#C6D92D] text-[#535353] dark:text-[#2D3748]"
                                                                : "hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] text-[#535353] dark:text-[#CEE0EF]"
                                                        }`}
                                                        onClick={() =>
                                                            setTableFilterOption(
                                                                "all"
                                                            )
                                                        }
                                                    >
                                                        Show All Features
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        className={`${
                                                            tableFilterOption ===
                                                            "selected"
                                                                ? "bg-[#C6D92D] dark:bg-[#C6D92D] hover:bg-[#C6D92D] hover:dark:bg-[#C6D92D] text-[#535353] dark:text-[#2D3748]"
                                                                : "hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] text-[#535353] dark:text-[#CEE0EF]"
                                                        }`}
                                                        onClick={() =>
                                                            setTableFilterOption(
                                                                "selected"
                                                            )
                                                        }
                                                    >
                                                        Show Selected Features
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        className={`${
                                                            tableFilterOption ===
                                                            "visible"
                                                                ? "bg-[#C6D92D] dark:bg-[#C6D92D] hover:bg-[#C6D92D] hover:dark:bg-[#C6D92D] text-[#535353] dark:text-[#2D3748]"
                                                                : "hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] text-[#535353] dark:text-[#CEE0EF]"
                                                        }`}
                                                        onClick={() =>
                                                            setTableFilterOption(
                                                                "visible"
                                                            )
                                                        }
                                                    >
                                                        Show Features Visible on
                                                        Map
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TabsList>
                                    </div>
                                    <TabsContent
                                        value="queue-projects"
                                        className="min-h-0 h-[40vh] max-w-[50vw]" // Setting reasons in Document/tables.md
                                    >
                                        <DataTable
                                            columns={queueProjectColumns}
                                            data={filteredIsoData || []}
                                            loading={isLoading}
                                        />
                                    </TabsContent>
                                    <TabsContent
                                        value="substation"
                                        className="min-h-0 h-[40vh] max-w-[50vw]"
                                    >
                                        <DataTable
                                            columns={substationColumns}
                                            data={substationData}
                                        />
                                    </TabsContent>
                                    <TabsContent
                                        value="transmission"
                                        className="min-h-0 h-[40vh] max-w-[50vw]"
                                    >
                                        <DataTable
                                            columns={transmissionLineColumns}
                                            data={transmissionLineData}
                                        />
                                    </TabsContent>
                                </Tabs>
                            </div>
                            <div className="w-1/2 mt-2 bg-white dark:bg-[#2D3748] rounded-[4px] flex flex-col h-full flex-1 min-h-0 p-2">
                                {currentISOFilter ? (
                                    <ConsolidatedDataTable
                                        columns={queueIsoColumns}
                                        data={formattedIsoData}
                                    />
                                ) : (
                                    <div className="flex flex-1 items-center justify-center w-full text-center">
                                        <span className="text-[16px] font-semibold text-[#535353] dark:text-[#CEE0EF] animate-pulse">
                                            Please select an ISO to view this
                                            table.
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </ReadAccessAuthorizedTemplate>
            </AuthenticatedTemplate>
        </div>
    );
};

export default QueueInsightsPage;
